<template>
  <Layout>
    <div class="row justify-content-center">
      <div class="col-md-6 p-5">
        <div class="mx-auto mb-3">
          <h4 class="d-inline align-middle text-center">
            Complete your invitation
          </h4>
          <p class="text-muted mb-0">Set your account details to continue.</p>
        </div>
        <b-alert show variant="danger" v-if="tokenError">{{ tokenError }}</b-alert>
        <div v-if="tokenLoading" class="text-center py-4">
          <b-spinner small class="mr-2"></b-spinner> Verifying invitation...
        </div>
        <b-form
          v-if="!tokenLoading && !tokenError"
          @submit.prevent="inviteUsers"
        >

          <TextField 
            placeholder="Enter Name"
            label="Company Name"
            name="company_name"
            v-model="model.company_name"
            v-if="model.recruitment_type == 'company'"
            :error="$v.model.company_name.$error"
            :errorMessage="$v.model.company_name.errorMessage"
          />

          <TextArea 
            placeholder="Enter Company Details..."
            label="Company details"
            name="company_details"
            v-model="model.company_details"
            v-if="model.recruitment_type == 'company'"
            :error="$v.model.company_details.$error"
            :errorMessage="$v.model.company_details.errorMessage"
          />

          <TextField 
            placeholder="Enter City"
            label="City"
            name="city"
            v-model="model.city"
            v-if="model.recruitment_type == 'company'"
          />

          <TextField 
            placeholder="Enter Country"
            label="Country"
            name="country"
            v-model="model.country"
            v-if="model.recruitment_type == 'company'"
          />

          <TextField 
            placeholder="Enter First Name"
            label="First Name"
            name="first_name"
            v-model="model.first_name"
            v-if="model.recruitment_type != 'company'"
            :error="$v.model.first_name.$error"
            :errorMessage="$v.model.first_name.errorMessage"
          />

          <TextField 
            placeholder="Enter Last Name"
            label="Last Name"
            name="last_name"
            v-model="model.last_name"
            v-if="model.recruitment_type != 'company'"
            :error="$v.model.first_name.$error"
            :errorMessage="$v.model.last_name.errorMessage"
          />

          <div class="form-group">
              <label class="form-label">Email</label>
              <input type="text" 
                readonly="" 
                class="form-control-plaintext"
                v-model="model.email"
              >
          </div>

          <TextField 
            placeholder="Password"
            label="Password *"
            name="password"
            type="password"
            v-model="model.password"
            :error="$v.model.password.$error"
            :errorMessage="passwordError"
          />

          <TextField 
            placeholder="Confirm Password"
            label="Confirm Password *"
            name="confirm"
            type="password"
            v-model="model.confirm_password"
            :error="$v.model.confirm_password.$error"
            :errorMessage="$v.model.confirm_password.errorMessage"
          />


          <div class="form-group">
            <button type="submit" class="btn btn-primary btn-block" :disabled="submitting">
              <i class="uil-check"></i> {{ submitting ? 'Submitting...' : 'Submit' }}
            </button>
          </div>

        </b-form>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@layouts/default'
import TextField from '@components/form/textfield'
import TextArea from '@components/form/textarea'
/* import InputTag from 'vue-input-tag' */
import { mapActions } from 'vuex'
import  AuthMixin from '@mixins/auth'
import  FormMixin from '@mixins/form'
import { required, sameAs, requiredIf } from 'vuelidate/lib/validators'
  
export default {
  mixins: [AuthMixin, FormMixin],
  data(){
    return {
      model:{
        id:'',
        email:'',
        first_name:'',
        last_name:'',
        password:'',
        confirm_password:''
      },
      passwordError: '',
      tokenLoading: true,
      tokenError: '',
      submitting: false,
    }
  },
  components:{Layout, TextField, TextArea},
  methods:{
    ...mapActions('user', ['findByToken', 'step_1']),
    async loadInvitation(){
      this.tokenLoading = true
      this.tokenError = ''
      try {
        const token = this.$route.query.key
        if (!token) {
          this.tokenError = 'Invitation token is missing.'
          return
        }
        const user = await this.findByToken({ token })
        const data = user.data.user
        this.model.email = data.email
        this.model.id = data._id
        this.model.recruitment_type = data.recruitment_type
        this.model.access_type = data.access_type
        if(user.data.step == 1){
          this.$router.push('/user/step_2?key=' + token)
        }
      } catch (err) {
        this.tokenError = err?.response?.data?.msg || 'Invitation is invalid or expired.'
      } finally {
        this.tokenLoading = false
      }
    },
    async inviteUsers(){
      if (this.validateForm()) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Submit your account details now?',
        {
          title: 'Confirm submission',
          okTitle: 'Submit',
          cancelTitle: 'Cancel',
          centered: true,
          okVariant: 'primary',
        }
      )
      if (!confirmed) return

      this.submitting = true
      try {
        const res = await this.step_1(this.model)
        if(res?.data?.done){
          await this.$bvModal.msgBoxOk(
            'Your account has been activated. You will be logged in automatically.',
            {
              title: 'Success',
              okTitle: 'Continue',
              centered: true,
              okVariant: 'primary',
            }
          )
          const loginRes = await this.login({
            email: this.model.email,
            password: this.model.password,
          })
          if (loginRes?.data?.success) {
            this.setAuth(loginRes.data)
          } else {
            this.$bvToast.toast(
              loginRes?.data?.message || 'Auto login failed. Please log in manually.',
              {
                title: 'Login Required',
                variant: 'warning',
                solid: true
              }
            )
            this.$router.push('/login')
          }
        }else if(res?.data?.ok){
          this.$router.push('/user/step_2?key=' + this.$route.query.key)
        }
      } catch (err) {
        this.$bvToast.toast(err?.response?.data?.msg || 'Unable to complete step 1. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.submitting = false
      }
    }
  },
  mounted() {
    this.loadInvitation()
  },
  validations: {
    model:{
      password: {
        required,
        passwordRule: function(value) {
          let valid = (/[A-Z]/.test(value) && /[a-z]/.test(value) && /[0-9]/.test(value) && /[#?!@$%^&*-]/.test(value) && value.length > 5)

          this.passwordError = (valid ? '' : 'Password must have at least (') + 
          (value.length > 5 ? '' : 'length of 6 characters; ') + 
          (/[A-Z]/.test(value) ? '' : '1 upper case letter; ') + 
          (/[a-z]/.test(value) ? '' : '1 lower case letter; ') + 
          (/[0-9]/.test(value)  ? '' : '1 number; ') + 
          (/[#?!@$%^&*-]/.test(value) ? '' : '1 special character') + 
          (valid ? '' : ')')
          
          return /[A-Z]/.test(value) && /[a-z]/.test(value) && /[0-9]/.test(value) && /[#?!@$%^&*-]/.test(value) && value.length > 5
        }
      },
      confirm_password: {
        sameAsPassword: sameAs('password')
      },
      first_name:{
        required: requiredIf(function(item){
          return item.recruitment_type == 'individual'
        })
      },
      last_name:{
        required: requiredIf(function(item){
          return item.recruitment_type == 'individual'
        })
      },
      company_name:{
        required: requiredIf(function(item){
          return item.recruitment_type == 'company'
        })
      },
      company_details:{
        required: requiredIf(function(item){
          return item.recruitment_type == 'company'
        })
      }
    }
  }
}
</script>